/**
 * The person's consent records (MM-D-138, TMMAPI-0156 U1): `GET /v1/me/consents` lists their own
 * rows; `POST /v1/me/consents {kind, version}` records one at first sign-in (context
 * `FIRST_SIGN_IN`). The POST is idempotent: a row of the same kind and version answers 200 as it
 * is, else 201. A version other than the current one answers `422 CONSENT_REQUIRED`
 * (`details.currentTermsVersion`, read by `consentRequired()` in `errors.ts`).
 */
import type {
  ConsentList as WireConsentList,
  ConsentRequest,
  ConsentView as WireConsent,
} from './generated';
import { request } from './client';

export type ConsentKind = ConsentRequest['kind'];

/** Both kinds a subscriber accepts, in the order the dialog asks them. */
export const CONSENT_KINDS: readonly ConsentKind[] = ['TERMS', 'AUTO_RENEWAL'];

export interface ConsentRecord {
  kind: ConsentKind;
  version: string;
}

/** A consent row; `null` (dropped) for a kind this app does not know. */
export function normalizeConsent(w: WireConsent): ConsentRecord | null {
  const kind = CONSENT_KINDS.find((known) => known === w.kind);
  if (kind === undefined) return null;
  return { kind, version: w.version };
}

export function normalizeConsentList(w: WireConsentList): ConsentRecord[] {
  return w.items.flatMap((row) => {
    const record = normalizeConsent(row);
    return record ? [record] : [];
  });
}

/** The kinds with no row for `version`, in `CONSENT_KINDS` order. */
export function missingConsentKinds(records: ConsentRecord[], version: string): ConsentKind[] {
  return CONSENT_KINDS.filter(
    (kind) => !records.some((row) => row.kind === kind && row.version === version),
  );
}

export const consentsApi = {
  async list(): Promise<ConsentRecord[]> {
    return normalizeConsentList(await request<WireConsentList>('GET', '/v1/me/consents'));
  },
  async record(kind: ConsentKind, version: string): Promise<ConsentRecord | null> {
    const body: ConsentRequest = { kind, version };
    return normalizeConsent(await request<WireConsent>('POST', '/v1/me/consents', { json: body }));
  },
};
